import { Location } from "vue-router";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import store from "@/store";
import { $t } from "@/lib/i18nHelpers";
import config from "@/config";
import { ROLES } from "@/const";

export enum MENU_MODULES {
  NSFW_USER_SETTINGS = "NSFW_USER_SETTINGS",
  SEPARATOR = "SEPARATOR",
}

interface FilterOptions {
  requiredRole?: string[];
  forbiddenRole?: string;
  requiredAuthorities?: string[];
  requiredCreditCard?: boolean;
}

type MenuItem = { title: string; hideBack: boolean; list: IMoreMenuItem[] };

export interface IMoreMenuItem {
  label: string;
  name: string;
  icon?: string;
  class?: string;
  to?: Location;
  href?: string;
  filter?: FilterOptions;
}

const defaultMenu: () => MenuItem = () => {
  const authorities = store.state.authorities;
  const user = store.state.user;

  return {
    title: $t("More"),
    hideBack: true,
    list: [
      {
        label: $t("Admin Dashboard"),
        name: "adminDashboard",
        href: "/admin",
        filter: {
          requiredAuthorities: ["canLoginToAdmin"],
        },
      },
      ...(!config.featureSoloModel
        ? [
            {
              label: $t("Become a Creator"),
              name: "becomeCreator",
              icon: "sci-rocket",
              class: "__become-creator",
              to: {
                name: "profilePayeeInfo",
              },
              filter: {
                requiredRole: ["member"],
              },
            },
          ]
        : []),
      {
        label: $t("Account settings"),
        name: "accountSettings",
        to: {
          query: {
            section: "account",
          },
        },
      },
      {
        label: $t("Notifications settings"),
        name: "notifications",
        to: {
          query: {
            section: "notifications",
          },
        },
      },
      {
        label: $t("Balance history"),
        name: "balanceHistory",
        to: {
          name: "balance",
        },
        filter: {
          forbiddenRole: "admin",
        },
      },
      {
        label: $t("Purchase gallery"),
        name: "purchases",
        to: {
          name: "purchases",
        },
        filter: {
          forbiddenRole: "admin",
          requiredAuthorities: ["getMyPurchasedFile"],
        },
        class: "__mobile-only",
      },
      ...(checkAuthorities(authorities ?? [], ["updateMyContentPrice", "getMyMassMessageStat"])
        ? [
            {
              label: $t("Mass messages statistics"),
              name: "massMessagesStatistics",
              to: {
                name: "massMessagesStatistics",
              },
              filter: {
                forbiddenRole: "admin",
                requiredAuthorities: ["getMyMassMessageStat"],
              },
            },
          ]
        : []),
      ...(config.featureStatements
        ? [
            {
              label: $t("Statements"),
              name: "statements",
              to: {
                name: "statements",
              },
              filter: {
                requiredRole: ["creator", "talent_agent"],
              },
            },
          ]
        : []),
      ...(config.featurePayoutRequest
        ? [
            {
              label: $t("Payout requests"),
              name: "payoutRequests",
              to: {
                name: "payoutRequests",
              },
              filter: {
                forbiddenRole: "admin",
                requiredAuthorities: ["getMyPayoutRequest"],
              },
            },
          ]
        : []),
      {
        label: $t("Subscription management"),
        name: "subscriptionManagement",
        to: {
          name: "manageRecurring",
        },
        filter: {
          forbiddenRole: "admin",
        },
      },
      {
        label: $t("Lists"),
        name: "groups",
        to: {
          name: "manageGroups",
        },
        filter: {
          forbiddenRole: "admin",
          requiredAuthorities: ["manageMyGroups"],
        },
      },
      ...(config.featureStories
        ? [
            {
              label: $t("Highlights archive"),
              name: "storiesArchive",
              to: {
                name: "storiesArchive",
              },
              filter: {
                requiredRole: ["creator"],
              },
            },
          ]
        : []),
      ...(config.featureEvents
        ? [
            {
              label: $t("Events"),
              name: "events",
              to: {
                name: "events",
              },
              filter: {
                forbiddenRole: "admin",
              },
            },
          ]
        : []),
      ...(config.featureAppointments
        ? [
            {
              label: $t("Appointments"),
              name: "dates",
              to: {
                name: "appointments",
                query: {
                  tab: checkRole(user?.role || "", ROLES.CREATOR) ? "requests" : "my-upcoming",
                },
              },
              filter: {
                forbiddenRole: "admin",
              },
            },
          ]
        : []),
      {
        label: $t("Vault"),
        name: "manageVault",
        to: {
          name: "manageVault",
        },
        filter: {
          forbiddenRole: "admin",
          requiredAuthorities: ["updateMyVault", "getMyVault"],
        },
      },
      ...(!config.featureSoloModel
        ? [
            {
              label: $t("Payee information"),
              name: "payeeInformation",
              to: {
                name: "profilePayeeInfo",
              },
              filter: {
                requiredRole: ["creator"],
              },
            },
          ]
        : []),
      ...(config.featureWelcomeMessage
        ? [
            {
              label: $t("Subscription and following"),
              name: "subAndFollow",
              to: {
                query: { section: "sub-following" },
              },
              filter: {
                forbiddenRole: "admin",
                requiredAuthorities: ["updateMyContentSubscriptionLevel"],
              },
            },
          ]
        : [
            {
              label: $t("Subscription levels"),
              name: "subscriptionLevels",
              to: {
                name: "profileSubscriptionLevels",
              },
              filter: {
                forbiddenRole: "admin",
                requiredAuthorities: ["updateMyContentSubscriptionLevel"],
              },
            },
          ]),
      {
        label: $t("Price settings"),
        name: "priceSettings",
        to: {
          name: "priceSettings",
        },
        filter: {
          forbiddenRole: "admin",
        },
      },
      {
        label: $t("Payment card"),
        name: "paymentCard",
        to: {
          name: "profilePaymentCards",
        },
        filter: {
          forbiddenRole: "admin",
          requiredCreditCard: true,
        },
      },
      {
        label: "",
        name: MENU_MODULES.SEPARATOR,
      },
      {
        label: $t("Invite friends"),
        name: "inviteFriends",
        to: {
          name: "referrals",
        },
        filter: {
          requiredRole: ["creator"],
        },
      },
      {
        label: $t("Creators by talent rep"),
        name: "creatorsByTalentRep",
        to: {
          name: "creatorsByTalentRep",
        },
        filter: {
          requiredRole: ["talent_agent"],
        },
      },
      {
        label: "",
        name: MENU_MODULES.SEPARATOR,
        filter: {
          requiredRole: ["creator"],
        },
      },
      ...(config.featureNSFW
        ? [
            {
              label: "",
              name: MENU_MODULES.NSFW_USER_SETTINGS,
            },
            {
              label: "",
              name: MENU_MODULES.SEPARATOR,
            },
          ]
        : []),
      {
        label: $t("Support"),
        name: "support",
        to: {
          name: "messages",
          params: {
            userId: "1",
          },
        },
        filter: {
          forbiddenRole: "admin",
        },
      },
      {
        label: "",
        name: MENU_MODULES.SEPARATOR,
        filter: {
          forbiddenRole: "admin",
        },
      },
      {
        label: $t("Terms & Policies"),
        name: "terms",
        to: {
          query: {
            section: "terms",
          },
        },
      },
      ...(config.featureFlagsDisplay
        ? [
            {
              label: $t("Features flags"),
              name: "featuresFlags",
              to: {
                name: "featuresFlags",
              },
            },
          ]
        : []),
      {
        label: "",
        name: MENU_MODULES.SEPARATOR,
      },
      {
        label: $t("Logout"),
        name: "logout",
        class: "__bleach",
        to: {
          name: "logout",
        },
      },
    ],
  };
};

const termsMenu: MenuItem = {
  hideBack: false,
  title: $t("Terms & Policies"),
  list: [],
};

const notificationsMenu: () => MenuItem = () => {
  const user = store.state.user;
  return {
    hideBack: false,
    title: $t("Notifications settings"),
    list: [
      ...(config.webPushNotificationsPublicKey
        ? [
            {
              label: $t("Push notifications"),
              name: "pushNotifications",
              to: {
                name: "pushNotificationsSettings",
              },
            },
          ]
        : []),
      ...(user?.email
        ? [
            {
              label: $t("E-mail notifications"),
              name: "emailNotifications",
              to: {
                name: "emailNotificationsSettings",
              },
            },
          ]
        : []),
    ],
  };
};

const accountMenu: () => MenuItem = () => {
  const user = store.state.user;
  return {
    hideBack: false,
    title: $t("Account settings"),
    list: [
      ...(user?.email
        ? [
            {
              label: user?.email,
              name: "userEmail",
              class: "__disabled",
            },
          ]
        : []),
      ...(user?.phone
        ? [
            {
              label: user?.phone || "",
              name: "userPhone",
              class: "__disabled",
            },
          ]
        : []),
      ...(user?.email
        ? [
            {
              label: $t("Change password"),
              name: "changePassword",
              to: {
                name: "changePassword",
              },
            },
          ]
        : []),
      {
        label: $t("Delete account"),
        name: "deleteAccount",
        to: {
          name: "deleteAccount",
        },
        filter: {
          requiredAuthorities: ["disableMyUser"],
        },
      },
    ],
  };
};

const subAndFollowing: MenuItem = {
  hideBack: false,
  title: $t("Subscription and following"),
  list: [
    {
      label: $t("Subscription levels"),
      name: "profileSubscriptionLevels",
      to: {
        name: "profileSubscriptionLevels",
      },
      filter: {
        requiredAuthorities: ["updateMyContentSubscriptionLevel"],
      },
    },
    {
      label: $t("Following"),
      name: "followingSettings",
      to: {
        name: "followingSettings",
      },
      filter: {
        requiredAuthorities: ["updateMyContentSubscriptionLevel"],
      },
    },
  ],
};

const moreMenu: Record<string, MenuItem> = {
  get "default"() {
    return defaultMenu();
  },
  "terms": termsMenu,
  "sub-following": subAndFollowing,
  get "account"() {
    return accountMenu();
  },
  get "notifications"() {
    return notificationsMenu();
  },
};

export { moreMenu };
